// Redeeming a teacher code.
//
// The code is checked and spent entirely by the accountRedeemCode function in
// the geezword-account project; this file only carries it there and turns the
// result into something a teacher can read. Nothing here grants access: when
// the function succeeds it writes the entitlement, and the listener in
// AuthContext flips Premium on by itself.

import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'
import { BILLING_MODE } from './access'

export const CODE_LENGTH = 16

// Shown as XXXX-XXXX-XXXX-XXXX while typing. The server normalises anyway, so
// this is for legibility when reading a code off a message or a slip of paper.
export function formatCodeInput(raw) {
  const clean = String(raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH)
  return clean.replace(/(.{4})(?=.)/g, '$1-')
}

export function codeIsComplete(raw) {
  return String(raw ?? '').replace(/[^A-Za-z0-9]/g, '').length === CODE_LENGTH
}

// One message per failure the teacher can act on, and deliberately **the same
// message for an unknown code and a spent one** — the function answers both
// identically so it cannot be used to discover valid codes, and saying "that
// code was already used" here would undo that.
const MESSAGES = {
  'invalid-argument': 'That code is not valid. Please check it and try again.',
  'not-found': 'That code is not valid. Please check it and try again.',
  'already-exists': 'This account already has teacher access.',
  'resource-exhausted': 'Too many attempts. Please wait an hour and try again.',
  'unauthenticated': 'Please sign in first, then enter your code.',
  'permission-denied': 'Please sign in with a verified email address.',
  'failed-precondition': 'Teacher codes are not available right now.',
}

export function redeemErrorMessage(err) {
  // Firebase prefixes callable error codes: 'functions/not-found'.
  const code = String(err?.code || '').replace(/^functions\//, '')
  return MESSAGES[code] || 'Something went wrong. Please try again.'
}

export async function redeemTeacherCode(code) {
  const call = httpsCallable(functions, 'accountRedeemCode')
  const { data } = await call({ mode: BILLING_MODE, code })
  return data
}
